import React, { useState } from 'react';
import { Box, Chip, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ISignageItem } from '../../types/signage';
import MediaThumb from './MediaThumb';
import { rulesLabel } from './playback';

interface PlaylistListProps {
  items: ISignageItem[];
  selectedId: string | null;
  canManage: boolean;
  onSelect: (id: string) => void;
  onToggle: (item: ISignageItem, enabled: boolean) => void;
  onReorder: (items: ISignageItem[]) => void;
}

export const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const PlaylistList: React.FC<PlaylistListProps> = ({ items, selectedId, canManage, onSelect, onToggle, onReorder }) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const drop = (targetId: string) => {
    const from = items.findIndex((item) => item.id === dragId);
    const to = items.findIndex((item) => item.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from >= 0 && to >= 0 && from !== to) onReorder(moveItem(items, from, to));
  };

  return (
    <Box role="list">
      {items.map((item, index) => (
        <Box
          key={item.id}
          role="listitem"
          data-testid={`playlist-row-${item.id}`}
          draggable={canManage}
          onDragStart={(e) => {
            setDragId(item.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={() => {
            setDragId(null);
            setOverId(null);
          }}
          onDragOver={(e) => {
            if (dragId) {
              e.preventDefault();
              setOverId(item.id);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            drop(item.id);
          }}
          onClick={() => onSelect(item.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: item.id === selectedId ? 'action.selected' : 'transparent',
            outline: overId === item.id && dragId !== item.id ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            outlineOffset: -2,
            opacity: dragId === item.id ? 0.4 : 1,
            '&:hover': { bgcolor: item.id === selectedId ? 'action.selected' : 'action.hover' },
          }}
        >
          {canManage && <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'grab' }} />}
          <Typography variant="caption" color="text.secondary" sx={{ width: 16, textAlign: 'center' }}>
            {index + 1}
          </Typography>
          <MediaThumb mediaType={item.mediaType} fileId={item.fileId} sourceUrl={item.sourceUrl} width={56} height={34} dimmed={!item.enabled} />
          <Box sx={{ flex: 1, minWidth: 0, opacity: item.enabled ? 1 : 0.5 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {item.title || '(untitled)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
              <Chip size="small" label={item.mediaType.toUpperCase()} sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary">
                {rulesLabel(item)}
              </Typography>
              {!item.enabled && <Chip size="small" color="warning" variant="outlined" label="HIDDEN" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />}
            </Box>
          </Box>
          {canManage && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" sx={{ p: 0 }} aria-label={`Move ${item.title || 'item'} up`} disabled={index === 0} onClick={() => onReorder(moveItem(items, index, index - 1))}>
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" sx={{ p: 0 }} aria-label={`Move ${item.title || 'item'} down`} disabled={index === items.length - 1} onClick={() => onReorder(moveItem(items, index, index + 1))}>
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
          <Switch
            size="small"
            checked={item.enabled}
            disabled={!canManage}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggle(item, e.target.checked)}
            slotProps={{ input: { 'aria-label': `${item.enabled ? 'Hide' : 'Show'} ${item.title || 'item'}` } }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default PlaylistList;
